// ColorfulText.tsx
import * as React from 'react';
import { Typography } from '@mui/material';

// defines red color in RGBA
const red: string = 'rgba(255, 0, 0, 1)';
// defines green color in RGBA
const green: string = 'rgba(58, 167, 62, 1)';
// defines available colors for the text characters
const availableRGBColors: string[] = ['red', 'green'];

function handleLastIndex(text: string, index: number) {
    // this function will return char from defined text
    // after the provided index if text is long enough
    // in case text is shorter than provided index will return empty string
    return text.length > index + 1 ? text.slice(index + 1, text.length) : '';
}

function pickColor(useColor: string) {
    // this function will return rgba color code based on color selection
    // TODO: maybe in future it can also be randomized?
    if(useColor === 'red') {
        return red;
    } else if(useColor === 'green') {
        return green;
    }
    // defines random index to define random color; in range of 0 / 1
    const randomBinary: 0 | 1 = Math.floor(Math.random() * 2) as 0 | 1;
    return pickColor(availableRGBColors[randomBinary]);
}

function getColorfulText(text: string, randomChars: boolean, skipChar: string[], useColor: string = 'random') {
    // defines available colors for the text characters
    const availableColors: string[] = ['red', 'green', 'random'];
    // validates that text must be provided and the selected color is defined
    if(text === undefined || text === '' || !availableColors.includes(useColor)) {
        return text;
    }
    // defines the first character index in a string
    let index: number = 0;
    // case if not random chars is used, meaning only first char that is not skipped will be colored
    if(!randomChars) {
        // get first char
        let firstChar: string = text[index];
        // check if first char is skipped
        while(skipChar.includes(firstChar)) {
            // if skipped then increment index counter and replace char
            index++;
            firstChar = text[index];
        }
        if(index > 0) {
            // in case char is skipped, preserve characters before the skipping
            // append the colored char
            // and add the rest of the text
            return `${text.slice(0, index)}<span style="color: ${pickColor(useColor)};">${firstChar}</span>${handleLastIndex(text, index)}`;
        }
        // in case char is not skipped, color the first character
        // and add the rest of the text
        return `<span style="color: ${pickColor(useColor)};">${firstChar}</span>${handleLastIndex(text, index)}`;
    } else {
        // case if random chars is used, meaning random characters for text will be colored
        // defines minimum distance from colored characters
        let minDistance: number = 20;
        // defines maximum distance from colored characters
        let maxDistance: number = 40;
        // defines new colored text
        let coloredText: string = '';
        // defines random index for the first character that will be colored; in range of 0 / minDistance
        let randomIndex: number = Math.floor(Math.random() * (minDistance - index + 1)) + index;
        // defines number array to store colored char indexes
        let coloredCharIndexes: number[] = [];
        for(let i = 0; i < text.length; i++) {
            // iterate given text
            if(coloredCharIndexes.length !== 0 && (coloredCharIndexes[coloredCharIndexes.length - 1] + maxDistance > i) && i !== randomIndex) {
                randomIndex = i;
            }
            if(randomIndex < i || randomIndex > i) {
                // append the chars that are not in range for coloring
                coloredText = `${coloredText}${text[i]}`;
                // continue the iteration cycle
                continue;
            } else {
                // validates that colored char is not in skip char list
                // validates that colored char is not a space
                // validates that colored char must be after the defined minimum distance
                if(!(skipChar.includes(text[i])) && text[i] !== ' ' && (coloredCharIndexes.length === 0 || (coloredCharIndexes[coloredCharIndexes.length - 1] + minDistance < i))) {
                    // append the colored char to the colored text
                    coloredText = `${coloredText}<span style="color: ${pickColor(useColor)};">${text[i]}</span>`;
                    // add index of colored char to defined array list
                    coloredCharIndexes = [...coloredCharIndexes, ...[i]];
                } else {
                    // append the chars that are not valid for coloring
                    coloredText = `${coloredText}${text[i]}`;
                }
                // set the new starting index
                index = i;
                // set the new random index of a char that may be colored using range of previous randomIndex to maximum of a text length
                randomIndex = Math.floor(Math.random() * (text.length - index + 1)) + index;
            }
        }
        // return the text with span color HTML element
        return `<span>${coloredText}</span>`;
    }
}

export function ColorfulText({ style, text, randomChars, skipChar, useColor = 'random' }: { style: any, text: string, randomChars: boolean, skipChar: string[], useColor: string }) {

    return (
        <Typography sx={style} dangerouslySetInnerHTML={{ __html: getColorfulText(text, randomChars, skipChar, useColor) }} />
    )
}
